import { cn } from '@/lib/utils'
import { Loader2 } from 'lucide-react'

interface PanelBodyProps {
  /** When true, show a loading overlay on top of content. */
  loading?: boolean
  /** Use a transparent overlay (content visible underneath). Default true. */
  transparentOverlay?: boolean
  className?: string
  children: React.ReactNode
}

/**
 * Standard panel body wrapper.
 * Provides a flex-1 container with an optional loading overlay.
 * Mirrors the old MosaicPanelBody: content is always mounted,
 * overlay sits absolutely positioned on top.
 */
export function PanelBody({
  loading = false,
  transparentOverlay = true,
  className,
  children,
}: PanelBodyProps) {
  return (
    <div className={cn('relative flex-1 min-h-0 min-w-0', className)}>
      <div
        className={cn(
          'h-full transition-opacity duration-300',
          loading && !transparentOverlay && 'opacity-0 pointer-events-none',
        )}
      >
        {children}
      </div>
      {loading && (
        <div
          className={cn(
            'absolute inset-0 z-10 flex justify-end p-3',
            // Opaque mode hides the content via the opacity toggle above, so
            // the overlay itself stays clear — the panel's gradient surface
            // shows through instead of a flat bg-card slab.
            'bg-transparent',
          )}
        >
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  )
}
